import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    EventEmitter,
    Inject,
    inject,
    Input,
    Output,
    signal,
    Signal,
    ViewChild,
    WritableSignal
} from '@angular/core';

import { IonButton, IonModal, IonRippleEffect, ModalController } from '@ionic/angular/standalone';

import { bytesToMegabytes } from '../../helpers';
import { LoadingService } from '../../services/loading/loading.service';
import { ProUserService } from '../../services/users/pro/pro.service';
import { DOCUMENT_TYPE_LABELS } from '../../shared';
import { DocumentObjectType, GlobalsProInterface, ProDocumentInterface } from '../../types';
import { ButtonComponent } from '../buttons/button/button.component';
import { CloseButtonComponent } from '../buttons/close-button/close-button.component';
import { IconButtonComponent } from '../buttons/icon-button/icon-button.component';
import { SvgIconComponent } from '../icons/svg-icon/svg-icon.component';
import { AlertModalContentComponent } from '../modals/alert-modal/alert-modal-content/alert-modal-content.component';

@Component({
    selector: 'lib-file-item',
    imports: [
        CommonModule,
        SvgIconComponent,
        IconButtonComponent,
        IonButton,
        IonRippleEffect,
        IonModal,
        CloseButtonComponent,
        ButtonComponent
    ],
    templateUrl: './file-item.component.html',
    styleUrl: './file-item.component.css'
})
export class FileItemComponent {
    /* eslint-disable */
    modalCtrl = inject(ModalController);
    proUserService = inject(ProUserService);
    loadingService = inject(LoadingService);

    @Input() file: DocumentObjectType | null = null;
    @Input() userFile: ProDocumentInterface | null = null;
    @Input() display: 'default' | 'existing-upload' = 'default';
    @Input() button: boolean = false;
    @Input() displayFileSize: boolean = true;
    @Input() displayExpiration: boolean = false;
    @Input() displayFileType: boolean = false;
    @Input() displayEndButtonOnInvalid: boolean = true;
    @Input() expirationDate: string = '';
    @Input() enableFileEdit: boolean = false;
    @Input() enableFilePreview: boolean = false;
    @Input() enableDocumentDeletion: boolean = true;

    @Output() onItemClick = new EventEmitter();
    @Output() onRemoveClick = new EventEmitter();
    @Output() onEndPlusButtonClick = new EventEmitter();
    @Output() onDocumentDelete = new EventEmitter();

    @ViewChild('previewModal') previewModal!: IonModal;

    public globals!: GlobalsProInterface;

    defaultExpiryYear: number = new Date().getFullYear() + 1;

    isUserFileValid: Signal<boolean> = computed(
        () => !!this.userFile && !this.userFile.is_expired && this.userFile.verified_status === 'approved'
    );

    documentTypeLabels = DOCUMENT_TYPE_LABELS;
    typeLabel: Signal<string | null> = computed(() => {
        const type = this.file?.type || this.userFile?.type;

        return type ? this.documentTypeLabels[type] : null;
    });

    displayedDeleteConfirmation: WritableSignal<boolean> = signal(false);

    constructor(@Inject('globals') globals: GlobalsProInterface) {
        this.globals = globals;
    }

    onButtonClick() {
        this.onItemClick.emit();

        if (this.enableFilePreview) {
            this.previewModal.present();
        }
    }

    onEndButtonClick(file: any) {
        if (this.display === 'default') {
            this.file = null;
        }
        this.onEndPlusButtonClick.emit();
    }

    onRemoveButtonClick(file: any) {
        if (this.display === 'default') {
            this.file = null;
        }
        this.onRemoveClick.emit();
    }

    async onDelete(id: number) {
        if (!this.enableDocumentDeletion || this.globals.appType !== 'pro') {
            return;
        }

        this.displayedDeleteConfirmation.set(true);

        const modal = await this.modalCtrl.create({
            component: AlertModalContentComponent,
            componentProps: {
                headerText: 'Remove document?',
                headerIcon: 'clipboard-remove-fill',
                message:
                    'Are you sure you want to remove this document? You may need to upload a new version to keep receiving jobs.',
                confirmButtonLabel: 'Remove document',
                cancelButtonLabel: 'Cancel'
            },
            focusTrap: true,
            cssClass: 'cancel-booking-modal modal-window'
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            this.displayedDeleteConfirmation.set(false);
        }

        if (data?.success) {
            if (this.globals.appType === 'pro') {
                this.loadingService.presentLoading();

                this.proUserService.removeDocumentFile(id).subscribe(() => {
                    this.loadingService.dismissLoading();
                    this.previewModal.dismiss();
                    this.userFile = null;
                    this.onDocumentDelete.emit(id);
                });
            }
        }
    }

    formatSize(size: number | null | undefined) {
        if (!size) return null;
        return bytesToMegabytes(size);
    }

    getFileNameFromUrl(url: string) {
        const splitUrl = url.split('/');
        return splitUrl[splitUrl.length - 1];
    }
}
